import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { getAdminUsersByRoleDetail } from "../../../../../utils/services/control/adminDashboardService";
import { formatDateTime } from "../../../../../utils/formatters/date";
import ExportExcelButton from "../../../../../components/Basic/ExportExcelButton";

const renderDays = (days) => {
  if (days === null || days === undefined) return "99+";
  return days;
};

const daysColor = (days) => {
  if (days === null || days === undefined) return "error";
  if (days >= 8) return "error";
  if (days >= 4) return "warning";
  return "success";
};

const UsersByRoleWidget = ({ rows, loading }) => {
  const [detailRole, setDetailRole] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [detailMessage, setDetailMessage] = useState("");

  const openDetail = async (role) => {
    setDetailRole(role);
    setDetailLoading(true);
    setDetailRows([]);
    setDetailMessage("");
    try {
      const res = await getAdminUsersByRoleDetail(
        role.roleId,
        role.status === 0
      );
      if (res?.result === 0) {
        setDetailRows(res.users || []);
      } else {
        setDetailMessage(res?.message || "Could not load role detail");
      }
    } catch (err) {
      setDetailMessage("Error loading role detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailRole(null);
    setDetailRows([]);
    setDetailMessage("");
  };

  const detailColumns = [
    { field: "fullName", headerName: "Name", flex: 1, minWidth: 180 },
    { field: "mail", headerName: "Email", flex: 1.2, minWidth: 200 },
    {
      field: "lastAccess",
      headerName: "Last Access",
      width: 170,
      renderCell: ({ value }) => formatDateTime(value, "Never"),
    },
    {
      field: "daysSinceLastAccess",
      headerName: "Days Idle",
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={renderDays(value)}
          color={daysColor(value)}
          variant="outlined"
        />
      ),
    },
  ];

  const exportColumns = [
    { field: "employeeId", headerName: "Employee ID" },
    { field: "fullName", headerName: "Full Name" },
    { field: "mail", headerName: "Email" },
    {
      field: "lastAccess",
      headerName: "Last Access",
      valueGetter: (r) => formatDateTime(r.lastAccess, "Never"),
    },
    {
      field: "daysSinceLastAccess",
      headerName: "Days Since Last Access",
      valueGetter: (r) =>
        r.daysSinceLastAccess === null || r.daysSinceLastAccess === undefined
          ? "Never"
          : r.daysSinceLastAccess,
    },
  ];

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Users by Role
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
          Click a chip to drill down. Inactive roles appear in gray.
        </Typography>
        {loading ? (
          <Stack direction="row" spacing={1}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" width={120} height={32} />
            ))}
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {(rows || []).map((r) => (
              <Chip
                key={r.roleId}
                label={`${r.roleName}: ${r.usersCount}`}
                onClick={() => openDetail(r)}
                color={r.status === 1 ? "primary" : "default"}
                variant={r.status === 1 ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  opacity: r.status === 1 ? 1 : 0.55,
                }}
              />
            ))}
          </Stack>
        )}
      </Paper>

      <Dialog
        open={Boolean(detailRole)}
        onClose={closeDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            Users assigned to <b>{detailRole?.roleName}</b>{" "}
            {detailRole?.status === 0 && (
              <Chip size="small" label="Inactive role" sx={{ ml: 1 }} />
            )}
          </Box>
          <IconButton onClick={closeDetail} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailMessage ? (
            <Typography color="error">{detailMessage}</Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: 1,
                }}
              >
                <ExportExcelButton
                  rows={detailRows}
                  columns={exportColumns}
                  filename={`users_role_${detailRole?.roleKey || "role"}`}
                />
              </Box>
              <Box sx={{ height: 420 }}>
                <DataGrid
                  rows={detailRows}
                  columns={detailColumns}
                  loading={detailLoading}
                  getRowId={(r) => r.employeeId}
                  density="compact"
                  hideFooterSelectedRowCount
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  sx={{ border: "none" }}
                />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersByRoleWidget;
